import React, { Children, lazy } from 'react';
import { ShieldCheck, Lock, FileText, Share2 } from 'lucide-react';
import { motion } from 'framer-motion';
export const FeaturesSection = () => {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };
  return <section className="py-24 bg-[#050505] relative overflow-hidden" id="features" data-id="element-2289">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none" data-id="element-2290" />

      <div className="max-w-6xl mx-auto px-6 relative z-10" data-id="element-2291">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-20" data-id="element-2292">
          <div className="inline-flex items-center gap-2 mb-6 bg-[#FF0000]/5 border border-[#FF0000]/20 px-3 py-1 rounded-full" data-id="element-2293">
            <span className="w-1.5 h-1.5 bg-[#FF0000] rounded-full animate-pulse" data-id="element-2294" />
            <span className="text-[#FF0000] font-mono text-[10px] tracking-widest uppercase font-bold" data-id="element-2295">
              System Capabilities
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-white mb-6 uppercase tracking-tight leading-none" data-id="element-2296">
            Why Choose <span className="text-[#FF0000]" data-id="element-2297">ToxicDetector</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed font-mono" data-id="element-2298">
            Forensic-grade analysis tools designed to decode manipulation
            patterns with absolute precision.
          </p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" data-id="element-2299">
          <motion.div variants={itemVariants} data-id="element-2300">
            <FeatureCard icon={<ShieldCheck size={24} data-id="element-2302" />} title="AI-Powered Forensics" description="Detects manipulation, gaslighting, and toxic patterns with the precision of a digital investigation." code="MOD_AI_01" data-id="element-2301" />
          </motion.div>
          <motion.div variants={itemVariants} data-id="element-2303">
            <FeatureCard icon={<Lock size={24} data-id="element-2305" />} title="Encrypted Privacy" description="Zero-knowledge architecture. Your conversations are encrypted, analyzed locally when possible, and never sold." code="SEC_LVL_5" data-id="element-2304" />
          </motion.div>
          <motion.div variants={itemVariants} data-id="element-2306">
            <FeatureCard icon={<FileText size={24} data-id="element-2308" />} title="Evidence Reports" description="Generate detailed PDF dossiers of toxic behaviors with timestamps, severity scores, and specific examples." code="DOC_GEN_V2" data-id="element-2307" />
          </motion.div>
          <motion.div variants={itemVariants} data-id="element-2309">
            <FeatureCard icon={<Share2 size={24} data-id="element-2311" />} title="Validation Cards" description="Create anonymous, data-backed result cards to validate your reality with friends or support groups." code="SOC_SHARE" data-id="element-2310" />
          </motion.div>
        </motion.div>

        {/* Forensic Dossier Visual Proof */}
        <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.98
      }} whileInView={{
        opacity: 1,
        y: 0,
        scale: 1
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.7,
        delay: 0.3
      }} className="mt-24 relative max-w-4xl mx-auto" data-id="element-2312">
          <div className="absolute -inset-1 bg-gradient-to-r from-[#FF0000]/10 via-[#FF0000]/5 to-[#FF0000]/10 rounded-2xl blur-xl opacity-50" data-id="element-2313" />

          <div className="relative bg-[#0a0a0a] rounded-xl border border-[#FF0000]/20 overflow-hidden shadow-[0_0_50px_rgba(255,0,0,0.1)] group" data-id="element-2314">
            <div className="absolute top-0 left-0 w-full h-1 bg-[#FF0000] opacity-50 z-20" data-id="element-2315" />

            <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden" data-id="element-2316">
              <img src="https://cdn.magicpatterns.com/uploads/spMpXkrZyRXy5uMrQdGBPT/file_00000000a198720c9cae59923f9b0abf_(2).png" alt="Forensic Dossier Report Example" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-all duration-700 group-hover:scale-105" loading="lazy" decoding="async" data-id="element-2317" />

              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-90" data-id="element-2318" />

              {/* Overlay Content */}
              <div className="absolute bottom-0 left-0 w-full p-8 md:p-10 flex flex-col md:flex-row justify-between items-end gap-6" data-id="element-2319">
                <div data-id="element-2320">
                  <div className="flex items-center gap-3 mb-2" data-id="element-2321">
                    <span className="bg-[#FF0000] text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider" data-id="element-2322">
                      Generated Output
                    </span>
                    <span className="text-gray-400 font-mono text-[10px] uppercase tracking-widest" data-id="element-2323">
                      Ref: DOC-8821
                    </span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black text-white uppercase tracking-tight mb-2" data-id="element-2324">
                    Your Evidence Package
                  </h3>
                  <p className="text-gray-400 font-mono text-sm max-w-md" data-id="element-2325">
                    Comprehensive PDF reports ready for therapists, lawyers, or
                    your own peace of mind.
                  </p>
                </div>

                <div className="flex items-center gap-4" data-id="element-2326">
                  <div className="text-right hidden md:block" data-id="element-2327">
                    <div className="text-[#FF0000] font-bold text-lg" data-id="element-2328">
                      CRITICAL
                    </div>
                    <div className="text-gray-500 text-[10px] uppercase tracking-wider" data-id="element-2329">
                      Risk Level
                    </div>
                  </div>
                  <div className="w-12 h-12 rounded-full border-2 border-[#FF0000] flex items-center justify-center bg-[#FF0000]/10 shadow-[0_0_15px_rgba(255,0,0,0.3)]" data-id="element-2330">
                    <span className="text-white font-bold text-sm" data-id="element-2331">92%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Scan Grid Overlay */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,0,0,0.03)_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none z-10" data-id="element-2332" />
          </div>
        </motion.div>
      </div>
    </section>;
};
interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  code: string;
}
const FeatureCard = ({
  icon,
  title,
  description,
  code
}: FeatureCardProps) => {
  return <motion.div whileHover={{
    y: -5
  }} className="relative bg-[#0a0a0a] p-8 md:p-10 border border-gray-800 group overflow-hidden h-full transition-colors hover:border-[#FF0000]/40" data-id="element-2333">
      {/* Corner Brackets */}
      <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-gray-700 group-hover:border-[#FF0000] transition-colors" data-id="element-2334" />
      <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-gray-700 group-hover:border-[#FF0000] transition-colors" data-id="element-2335" />
      <div className="absolute bottom-0 left-0 w-4 h-4 border-b border-l border-gray-700 group-hover:border-[#FF0000] transition-colors" data-id="element-2336" />
      <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-gray-700 group-hover:border-[#FF0000] transition-colors" data-id="element-2337" />

      {/* Hover Glow Background */}
      <div className="absolute inset-0 bg-[#FF0000]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-2338" />

      <div className="relative z-10 flex flex-col sm:flex-row items-start gap-6" data-id="element-2339">
        <div className="flex-shrink-0" data-id="element-2340">
          <div className="w-14 h-14 bg-[#FF0000]/5 border border-[#FF0000]/20 flex items-center justify-center text-[#FF0000] group-hover:bg-[#FF0000] group-hover:text-white transition-all duration-300 shadow-[0_0_15px_rgba(255,0,0,0.05)] group-hover:shadow-[0_0_20px_rgba(255,0,0,0.4)] rounded-sm" data-id="element-2341">
            {icon}
          </div>
        </div>

        <div className="flex-1" data-id="element-2342">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2" data-id="element-2343">
            <h3 className="text-xl font-bold text-white group-hover:text-[#FF0000] transition-colors uppercase tracking-wide" data-id="element-2344">
              {title}
            </h3>
            <span className="text-[10px] font-mono text-gray-600 group-hover:text-[#FF0000]/70 transition-colors border border-gray-800 group-hover:border-[#FF0000]/30 px-2 py-0.5 w-fit" data-id="element-2345">
              {code}
            </span>
          </div>
          <p className="text-gray-400 leading-relaxed font-mono text-sm group-hover:text-gray-300 transition-colors" data-id="element-2346">
            {description}
          </p>
        </div>
      </div>

      {/* Scanning Line on Hover */}
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#FF0000] to-transparent opacity-0 group-hover:opacity-50 group-hover:animate-[scan_2s_ease-in-out_infinite]" data-id="element-2347" />
    </motion.div>;
};